import {
  ConnectedAccountResource,
  ConnectedAccountStatus,
  ConnectedAccountTypeInfo,
  omitUnchangedSensitiveFields,
} from "@arrowsup/platform-dtos";
import { Button } from "@mui/material";
import { useState } from "react";
import {
  ChipField,
  DateField,
  PasswordInput,
  SelectInput,
  TextField,
  TextInput,
  useNotify,
  useRecordContext,
  useRefresh,
} from "react-admin";
import { apiErrorMessage } from "../../logic/api/api-error-message";
import { omit } from "../../logic/omit";

/**
 * Pieces every connected account's pages share: an ad account's, for one.
 * `noun` is what a person calls one ("ad account").
 */

export const connectedAccountTypeChoices = <T extends string>(
  types: Record<T, ConnectedAccountTypeInfo>
) =>
  (Object.keys(types) as T[]).map((type) => ({
    id: type,
    name: types[type].label,
  }));

const inactiveStatusLabels: Record<ConnectedAccountStatus, string> = {
  active: "active",
  access_lost: "access lost",
  disabled: "disabled",
};

/** `name`, noting the account's status unless it's active, for pickers. */
export const nameWithStatus = (
  name: string,
  status: ConnectedAccountStatus | undefined
): string =>
  status && status !== "active"
    ? `${name} (${inactiveStatusLabels[status]})`
    : name;

/** Set by Arrows Up, never sent back on save. */
const readOnlyFields = [
  "statusDetail",
  "lastVerifiedAt",
  "createdAt",
  "updatedAt",
];

/**
 * An edit form's values to save: without fields Arrows Up sets, or
 * `fixedFields`, and with the API key only when it's being replaced.
 */
export const connectedAccountChanges = (
  data: Record<string, unknown>,
  fixedFields: readonly string[]
) =>
  omitUnchangedSensitiveFields(
    omit(data, [...readOnlyFields, ...fixedFields]),
    ["apiKey"]
  );

/** List columns for an account's status. Datagrid needs them unwrapped. */
export const connectedAccountStatusColumns = () => [
  <ChipField key="status" source="status" />,
  <TextField key="statusDetail" source="statusDetail" label="Status Detail" />,
  <DateField
    key="lastVerifiedAt"
    source="lastVerifiedAt"
    label="Last Verified"
    showTime
  />,
];

/** The key an account is added with. It can't be read back. */
export const ApiKeyInput: React.FC<{ helperText: string }> = ({
  helperText,
}) => (
  <PasswordInput
    source="apiKey"
    label="API Key"
    helperText={`${helperText} It can't be viewed later.`}
    required
    fullWidth
  />
);

/** Shown masked. A new key replaces it once Arrows Up checks it works. */
export const ReplaceApiKeyInput: React.FC<{ noun: string }> = ({ noun }) => (
  <PasswordInput
    source="apiKey"
    label="API Key"
    helperText={`The current key can't be viewed. Enter a new one to replace it; Arrows Up checks it reaches this ${noun} before saving.`}
    fullWidth
  />
);

/** `whenDisabled` says what disabling one does. */
export const ConnectedAccountStatusInput: React.FC<{
  whenDisabled: string;
}> = ({ whenDisabled }) => (
  <>
    <SelectInput
      source="status"
      choices={[
        { id: "active", name: "Active" },
        { id: "disabled", name: "Disabled" },
        { id: "access_lost", name: "Access lost", disabled: true },
      ]}
      helperText={`${whenDisabled} Setting one active checks Arrows Up can still reach it.`}
    />
    <TextInput source="statusDetail" label="Status Detail" disabled fullWidth />
  </>
);

/** Checks Arrows Up can still reach the account, recording the result. */
export const VerifyAccessButton: React.FC<{
  noun: string;
  verify: (id: number) => Promise<unknown>;
}> = ({ noun, verify }) => {
  const record = useRecordContext<ConnectedAccountResource>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);

  if (!record) {
    return null;
  }

  const run = async () => {
    setBusy(true);
    try {
      await verify(record.id);
      notify(`Arrows Up can reach this ${noun}`);
    } catch (e) {
      notify(apiErrorMessage(e), { type: "error" });
    } finally {
      setBusy(false);
      refresh();
    }
  };

  return (
    <Button onClick={() => void run()} disabled={busy} sx={{ mb: 2 }}>
      Verify access now
    </Button>
  );
};
